/**
 * シミュレーション本体（docs/02_architecture.md 6〜8 章、docs/03_simulation.md）。
 * 状態の変更はコマンドだけで行い、外への通知は出来事（SimEvent）だけで行う。
 */
import {
  COURSE_RULES,
  FLIGHT,
  GUST,
  LAUNCH,
  PACK,
  STAMINA,
  TIME,
  BIOME_HIKE_STAMINA,
  RAIN_HIKE_PENALTY,
} from '../config/balance';
import type { AthleteSetup, Personality } from '../config/rivals';
import { DAY_PROFILES, type DayProfile } from '../config/weatherProfiles';
import { createRng, type Rng } from '../core/rng';
import type { Vec2 } from '../core/vec';
import { Biome, type NodeData, type WorldData } from '../core/world';
import { edgeTimeS, segmentSpeedMs, type HikeModifiers } from './athlete/hike';
import {
  downStaminaFactor,
  flatDownFactor,
  nightRecoveryFactor,
  prepPackFactor,
  rollWithLuck,
  sinkFactor,
  staminaSpeedFactor,
  staminaUseFactor,
  statLerp,
  uphillFactor,
  weatherMitigation,
} from './athlete/stats';
import { autopilot } from './control/autopilot';
import type { Command } from './control/commands';
import { decidePlan } from './control/cpuBrain';
import type { SimEvent, SimEventBody } from './events';
import { checkLaunch } from './flight/launch';
import { polarAt, stepFlightPhysics, type ControlInput } from './flight/physics';
import { computeStandings, makeCourse, progressOf, type Course } from './race/progress';
import { targetTimeScale } from './race/timeScale';
import type { AthleteState, FlightState, PlanStep, RaceState } from './types';
import type { Forecast } from './weather/forecast';
import { WeatherModel } from './weather/model';
import { segmentAt, pointAlongEdge } from './world/edgeGeometry';
import { buildGraph, type Graph } from './world/graph';
import { findPath } from './world/pathfind';
import { ensureNodeAt } from './world/tempNodes';
import { createTerrainSampler, type TerrainSampler } from './world/terrain';

export interface SimOptions {
  weatherSeed: number;
  roster: AthleteSetup[];
  profiles?: DayProfile[];
}

export class Sim {
  readonly graph: Graph;
  readonly terrain: TerrainSampler;
  readonly weather: WeatherModel;
  readonly course: Course;
  readonly race: RaceState;
  readonly athletes: AthleteState[];
  forecast: Forecast;

  private readonly profiles: DayProfile[];
  private readonly personalities = new Map<string, Personality>();
  private readonly rngWeather: Rng;
  private readonly rngAthlete = new Map<string, Rng>();
  private readonly rngCpu = new Map<string, Rng>();
  private readonly nextDecisionAt = new Map<string, number>();
  private readonly replanAtNode = new Set<string>();
  private readonly climbStart = new Map<string, number>();
  /** 追い風の離陸の連続失敗回数（2 回失敗したら次は必ず成功させる） */
  private readonly launchFails = new Map<string, number>();
  private commands: Command[] = [];
  private eventBuf: SimEvent[] = [];
  private tickAcc = 0;
  private tickCount = 0;
  private groundAcc = 0;
  private weatherAcc = 0;
  private standingsAcc = 0;
  private readonly input: ControlInput = { brakeL: 0, brakeR: 0, bar: 0, bigEars: 0 };
  private readonly tmpWind: Vec2 = { x: 0, z: 0 };

  constructor(
    readonly world: WorldData,
    opts: SimOptions,
  ) {
    this.profiles = opts.profiles ?? DAY_PROFILES;
    this.graph = buildGraph(world);
    this.terrain = createTerrainSampler(world);
    const stormSites = world.nodes
      .filter((n) => n.kind === 'launchPeak')
      .map((n) => ({ x: n.pos.x, z: n.pos.z }));
    this.weather = new WeatherModel(this.terrain, world, stormSites);
    const cpNodes = world.course.nodeIds.map((id) => world.nodes[id] as NodeData);
    this.course = makeCourse(
      cpNodes.map((n) => ({ x: n.pos.x, z: n.pos.z })),
      world.course.nodeIds,
    );
    this.rngWeather = createRng(opts.weatherSeed, 'weather');

    const start = cpNodes[0] as NodeData;
    this.athletes = opts.roster.map((r) => {
      this.personalities.set(r.id, r.personality);
      this.rngAthlete.set(r.id, createRng(opts.weatherSeed, `athlete:${r.id}`));
      this.rngCpu.set(r.id, createRng(opts.weatherSeed, `cpu:${r.id}`));
      return {
        id: r.id,
        name: r.name,
        isPlayer: r.isPlayer,
        color: r.color,
        stats: { ...r.stats },
        stamina: 100,
        mode: 'idle',
        pos: { ...start.pos },
        nodeId: start.id,
        route: null,
        current: null,
        plan: [],
        timerS: 0,
        flight: null,
        autoStyle: r.personality.autoStyle,
        nextCp: 1,
        progressM: 0,
        bestProgressM: 0,
        progressLog: [0, 0],
        finishTimeS: null,
        day: { flights: 0, longestFlightM: 0, crashes: 0, startProgressM: 0 },
        blockedLaunches: [],
      } satisfies AthleteState;
    });

    this.race = {
      dayIndex: 0,
      phase: 'planning',
      timeOfDayS: TIME.dayStartS,
      activeRaceTimeS: 0,
      pause: null,
      fastForward: 1,
      editing: false,
      standings: [],
      leaderId: null,
    };
    this.forecast = this.weather.startDay(this.profiles[0] as DayProfile, 0, this.rngWeather, TIME.dayStartS);
    this.race.standings = computeStandings(this.course, this.athletes, 0);
    this.emit({ type: 'dayStart' });
    this.planCpus();
  }

  // ---- 外とのやりとり ----

  command(c: Command): void {
    this.commands.push(c);
  }

  drainEvents(): SimEvent[] {
    const out = this.eventBuf;
    this.eventBuf = [];
    return out;
  }

  targetTimeScale(): number {
    return targetTimeScale(this.race);
  }

  athlete(id: string): AthleteState | undefined {
    return this.athletes.find((a) => a.id === id);
  }

  hikeMods(a: AthleteState): HikeModifiers {
    const rain = this.weather.rainAt(a.pos.x, a.pos.z)
      ? 1 - RAIN_HIKE_PENALTY * (1 - weatherMitigation(a.stats))
      : 1;
    return {
      uphill: uphillFactor(a.stats),
      flatDown: flatDownFactor(a.stats),
      overall: staminaSpeedFactor(a.stamina) * rain,
    };
  }

  /** 新しい予約の起点になるノードと、そこに着くまでの時間（プレイヤーの計画用） */
  anchor(a: AthleteState): { nodeId: number | null; etaS: number } {
    if (a.mode === 'hiking' && a.route) {
      const arc = a.route.arcs[a.route.index];
      if (arc) {
        const e = this.graph.edges[arc.edgeId];
        const remaining = e
          ? edgeTimeS(e, arc.forward, this.hikeMods(a)) * (1 - a.route.distM / e.lengthM)
          : 0;
        return { nodeId: arc.to, etaS: remaining };
      }
    }
    if (a.mode === 'flying' && a.flight) return { nodeId: a.flight.targetNodeId, etaS: 0 };
    if (a.mode === 'preparing' || a.mode === 'packing')
      return { nodeId: a.nodeId, etaS: Math.max(0, a.timerS) };
    return { nodeId: a.nodeId, etaS: 0 };
  }

  /** 時間を進める（ゲーム内秒）。作戦フェーズ・強制ポーズ・日の終わりでは進まない */
  advance(gameDtS: number): void {
    this.applyCommands();
    if (!this.isTicking()) {
      this.tickAcc = 0;
      return;
    }
    this.tickAcc += gameDtS;
    let ticks = 0;
    while (this.tickAcc >= TIME.tickS && ticks < TIME.maxTicksPerAdvance) {
      this.tickAcc -= TIME.tickS;
      this.tick(TIME.tickS);
      ticks++;
      if (!this.isTicking()) {
        this.tickAcc = 0;
        break;
      }
    }
    if (ticks >= TIME.maxTicksPerAdvance) this.tickAcc = 0;
  }

  private isTicking(): boolean {
    return (this.race.phase === 'running' || this.race.phase === 'sunsetLanding') && !this.race.pause;
  }

  private emit(body: SimEventBody): void {
    this.eventBuf.push({
      ...body,
      t: this.race.activeRaceTimeS,
      dayIndex: this.race.dayIndex,
      timeOfDayS: this.race.timeOfDayS,
    } as SimEvent);
  }

  private applyCommands(): void {
    const cmds = this.commands;
    this.commands = [];
    for (const c of cmds) {
      switch (c.type) {
        case 'setPlan': {
          const a = this.athlete(c.athleteId);
          if (a) this.setPlan(a, c.steps);
          break;
        }
        case 'setAutoStyle': {
          const a = this.athlete(c.athleteId);
          if (a) a.autoStyle = c.style;
          break;
        }
        case 'setEditing':
          this.race.editing = c.editing;
          break;
        case 'setFastForward':
          this.race.fastForward = c.level;
          break;
        case 'start':
          if (this.race.phase === 'planning') {
            this.race.phase = 'running';
            if (this.race.dayIndex === 0) this.emit({ type: 'raceStart' });
          }
          break;
        case 'resume':
          this.race.pause = null;
          break;
        case 'nextDay':
          if (this.race.phase === 'dayEnd') this.startNextDay();
          break;
      }
    }
  }

  private setPlan(a: AthleteState, steps: PlanStep[]): void {
    const plan = steps.map((s) => ({ ...s }));
    if (a.mode === 'hiking' && a.route) {
      // 今の弧は歩き切り、その先から新しい予約に従う
      const arc = a.route.arcs[a.route.index];
      if (arc) {
        a.route.arcs = a.route.arcs.slice(0, a.route.index + 1);
        a.current = { kind: 'hike', to: arc.to };
      }
      a.plan = plan;
    } else if (a.mode === 'preparing' || a.mode === 'waitingWind') {
      const first = plan[0];
      if (first?.kind === 'flight') {
        a.current = first;
        a.plan = plan.slice(1);
      } else {
        a.mode = 'idle';
        a.current = null;
        a.plan = plan;
      }
    } else if (a.mode === 'flying' && a.flight) {
      const first = plan[0];
      if (first?.kind === 'flight') {
        const t = this.graph.nodes[first.targetNodeId];
        if (t) {
          a.flight.target = { x: t.pos.x, z: t.pos.z };
          a.flight.targetNodeId = t.id;
        }
        a.plan = plan.slice(1);
      } else a.plan = plan;
    } else {
      a.plan = plan;
    }
  }

  // ---- 1 刻み ----

  private tick(dt: number): void {
    const race = this.race;
    race.timeOfDayS += dt;
    race.activeRaceTimeS += dt;
    this.tickCount++;

    for (const a of this.athletes) if (a.mode === 'flying') this.updateFlight(a, dt);

    this.groundAcc += dt;
    if (this.groundAcc >= TIME.hikeStepS) {
      this.groundAcc -= TIME.hikeStepS;
      for (const a of this.athletes) {
        if (a.mode !== 'flying') this.updateGround(a, TIME.hikeStepS);
        this.updateProgress(a);
      }
    }

    this.weatherAcc += dt;
    if (this.weatherAcc >= TIME.weatherStepS) {
      this.weatherAcc -= TIME.weatherStepS;
      this.weather.step(race.timeOfDayS, TIME.weatherStepS, this.rngWeather, (e) => {
        this.emit(e);
        if (e.type === 'cuDeveloping' || e.type === 'stormFormed') this.flagStormReplan(e.x, e.z);
      });
    }

    this.standingsAcc += dt;
    if (this.standingsAcc >= TIME.standingsStepS) {
      this.standingsAcc -= TIME.standingsStepS;
      this.updateStandings();
    }

    if (race.phase === 'running' && race.timeOfDayS >= TIME.sunsetS) this.beginSunset();
    if (race.timeOfDayS >= TIME.bivouacS) this.endDay();
    if (this.athletes.every((a) => a.finishTimeS !== null)) this.endRace();
  }

  // ---- 地上 ----

  private updateGround(a: AthleteState, dt: number): void {
    switch (a.mode) {
      case 'idle':
        this.startNextStep(a);
        break;
      case 'hiking':
        this.updateHike(a, dt);
        break;
      case 'preparing':
        a.timerS -= dt;
        if (a.timerS <= 0) this.tryLaunch(a);
        break;
      case 'waitingWind': {
        a.timerS += dt;
        if (a.timerS % LAUNCH.windCheckS < dt) {
          const node = this.graph.nodes[a.nodeId ?? -1];
          const check = node ? checkLaunch(this.weather, node, this.race.timeOfDayS, a.stats) : null;
          if (check?.ok) {
            a.mode = 'preparing';
            a.timerS = LAUNCH.prepS * prepPackFactor(a.stats);
          } else if (a.timerS >= LAUNCH.windWaitMaxS) this.abortTakeoff(a, 'wind');
        }
        break;
      }
      case 'packing':
        a.timerS -= dt;
        if (a.timerS <= 0) {
          a.mode = 'idle';
          this.emit({ type: 'packed', athleteId: a.id });
        }
        break;
      default:
        break;
    }
  }

  private startNextStep(a: AthleteState): void {
    if (a.finishTimeS !== null || a.nodeId === null) return;
    let step = a.plan.shift();
    if (!step) {
      if (!a.isPlayer) this.decideCpu(a);
      return;
    }
    // CP は順番に地上で通過する。まだ通っていない CP より先の CP へ歩こうとしたら、先に次の CP へ歩く
    if (step.kind === 'hike') {
      const k = this.course.nodeIds.indexOf(step.to);
      const next = this.course.nodeIds[a.nextCp];
      if (k > a.nextCp && next !== undefined && next !== a.nodeId) {
        a.plan.unshift(step);
        step = { kind: 'hike', to: next };
      }
    }
    a.current = step;
    if (step.kind === 'hike') {
      if (a.nodeId === step.to) {
        a.current = null;
        return;
      }
      const path = findPath(this.graph, a.nodeId, step.to, (e, fwd) => edgeTimeS(e, fwd, this.hikeMods(a)));
      if (!path || path.arcs.length === 0) {
        a.current = null;
        return;
      }
      a.route = { arcs: path.arcs, index: 0, distM: 0 };
      a.mode = 'hiking';
      a.nodeId = null;
    } else {
      this.startTakeoff(a, step);
    }
  }

  private updateHike(a: AthleteState, dt: number): void {
    const route = a.route;
    if (!route) {
      a.mode = 'idle';
      return;
    }
    const mods = this.hikeMods(a);
    const rain = this.weather.rainAt(a.pos.x, a.pos.z) ? STAMINA.rainFactor : 1;
    let remaining = dt;
    let staminaUse = 0;
    while (remaining > 1e-6) {
      const arc = route.arcs[route.index];
      const edge = arc ? this.graph.edges[arc.edgeId] : undefined;
      if (!arc || !edge) break;
      const L = edge.lengthM;
      const fromA = arc.forward ? route.distM : L - route.distM;
      const seg = segmentAt(edge, arc.forward ? fromA : Math.max(fromA - 0.01, 0));
      const grade = arc.forward ? seg.grade : -seg.grade;
      const v = segmentSpeedMs(grade, seg.biome, mods);
      if (v <= 0) {
        route.distM = L;
      } else {
        const step = Math.min(v * remaining, L - route.distM);
        route.distM += step;
        remaining -= step / v;
        const bs = BIOME_HIKE_STAMINA[seg.biome] ?? 1;
        let use = (STAMINA.hikePerHour * (step / v)) / 3600;
        if (grade > 0) use += ((grade * step) / 100) * STAMINA.per100mUp;
        else use += ((-grade * step) / 100) * STAMINA.per100mDown * downStaminaFactor(a.stats);
        staminaUse += use * bs;
      }
      if (route.distM >= L - 1e-6) {
        route.index++;
        route.distM = 0;
        const flagged = this.replanAtNode.has(a.id);
        if (route.index >= route.arcs.length || flagged) {
          this.arriveAtNode(a, arc.to);
          if (flagged) {
            this.replanAtNode.delete(a.id);
            a.plan = [];
            a.current = null;
          }
          break;
        }
      }
    }
    this.useStamina(a, staminaUse * staminaUseFactor(a.stats) * rain);
    if (a.route) {
      const arc = a.route.arcs[a.route.index];
      const edge = arc ? this.graph.edges[arc.edgeId] : undefined;
      if (arc && edge)
        pointAlongEdge(edge, arc.forward ? a.route.distM : edge.lengthM - a.route.distM, a.pos);
    }
  }

  private arriveAtNode(a: AthleteState, nodeId: number): void {
    const node = this.graph.nodes[nodeId] as NodeData;
    a.nodeId = nodeId;
    a.route = null;
    a.mode = 'idle';
    a.current = null;
    a.pos = { ...node.pos };
    this.emit({ type: 'arrived', athleteId: a.id, nodeId });
  }

  private useStamina(a: AthleteState, amount: number): void {
    const before = a.stamina;
    a.stamina = Math.min(100, Math.max(0, a.stamina - amount));
    if (before >= STAMINA.lowThreshold && a.stamina < STAMINA.lowThreshold)
      this.emit({ type: 'staminaLow', athleteId: a.id });
  }

  // ---- 離陸 ----

  private startTakeoff(a: AthleteState, step: Extract<PlanStep, { kind: 'flight' }>): void {
    const node = this.graph.nodes[a.nodeId ?? -1];
    if (!node?.launch) return this.abortTakeoff(a, 'notLaunch');
    if (this.race.phase !== 'running') return this.abortTakeoff(a, 'sunset');
    a.current = step;
    const check = checkLaunch(this.weather, node, this.race.timeOfDayS, a.stats);
    if (check.ok) {
      a.mode = 'preparing';
      a.timerS = LAUNCH.prepS * prepPackFactor(a.stats);
      this.emit({ type: 'prepStarted', athleteId: a.id, level: node.launch.level });
    } else {
      a.mode = 'waitingWind';
      a.timerS = 0;
      this.emit({
        type: 'windWaitStarted',
        athleteId: a.id,
        reason: check.reason ?? 'strong',
        windKmh: Math.round(check.windKmh),
      });
    }
  }

  private abortTakeoff(a: AthleteState, reason: 'wind' | 'sunset' | 'notLaunch'): void {
    const step = a.current;
    if (a.nodeId !== null && reason === 'wind') {
      a.blockedLaunches.push({ nodeId: a.nodeId, untilS: this.race.activeRaceTimeS + 3600 });
    }
    a.mode = 'idle';
    a.current = null;
    a.timerS = 0;
    // 飛べなかったら、飛ぶはずだった先へ歩いて斜面を下る（来た道を引き返させない）
    const walkTo =
      reason !== 'notLaunch' &&
      step?.kind === 'flight' &&
      step.targetNodeId !== a.nodeId &&
      this.graph.nodes[step.targetNodeId]
        ? step.targetNodeId
        : null;
    if (walkTo !== null) a.plan.unshift({ kind: 'hike', to: walkTo });
    this.emit({ type: 'takeoffAborted', athleteId: a.id, reason, walkToNodeId: walkTo });
  }

  private tryLaunch(a: AthleteState): void {
    const node = this.graph.nodes[a.nodeId ?? -1];
    const step = a.current;
    if (!node?.launch || step?.kind !== 'flight') return this.abortTakeoff(a, 'notLaunch');
    if (this.race.phase !== 'running') return this.abortTakeoff(a, 'sunset');
    const check = checkLaunch(this.weather, node, this.race.timeOfDayS, a.stats);
    if (!check.ok) {
      a.mode = 'waitingWind';
      a.timerS = 0;
      this.emit({
        type: 'windWaitStarted',
        athleteId: a.id,
        reason: check.reason ?? 'strong',
        windKmh: Math.round(check.windKmh),
      });
      return;
    }
    const rng = this.rngAthlete.get(a.id) as Rng;
    const fails = this.launchFails.get(a.id) ?? 0;
    if (fails < 2) {
      // 強風ぎりぎり、または追い風・横風の離陸は失敗しやすい（2 回失敗したら次は必ず成功）
      const pStrong =
        check.nearLimit > LAUNCH.failFrom
          ? (LAUNCH.failMaxP * (check.nearLimit - LAUNCH.failFrom)) / (1 - LAUNCH.failFrom)
          : 0;
      const p = Math.max(pStrong, check.tailwindFailP);
      if (p > 0 && rollWithLuck(rng, a.stats, p)) {
        const tail = check.tailwindFailP >= pStrong;
        this.launchFails.set(a.id, fails + 1);
        this.emit({ type: 'takeoffFailed', athleteId: a.id, reason: tail ? 'tailwind' : 'strong' });
        this.useStamina(a, tail ? LAUNCH.tailwind.stamina : LAUNCH.failStamina);
        a.timerS = tail ? LAUNCH.tailwind.lossS : LAUNCH.failLossS;
        return;
      }
    }
    this.launchFails.set(a.id, 0);
    const target =
      this.graph.nodes[step.targetNodeId] ??
      (this.graph.nodes[this.course.nodeIds[a.nextCp] ?? 0] as NodeData);
    const facing = check.headingRad;
    const flight: FlightState = {
      pos: {
        x: node.pos.x + Math.sin(facing) * 20,
        y: node.pos.y + 15,
        z: node.pos.z - Math.cos(facing) * 20,
      },
      headingRad: facing,
      airspeedMs: polarAt(0).speedMs,
      bankRad: 0,
      brakeL: 0,
      brakeR: 0,
      bar: 0,
      varioMs: 0,
      liftMs: 0,
      stall: null,
      highBrakeS: 0,
      gust: null,
      circlingS: 0,
      target: { x: target.pos.x, z: target.pos.z },
      targetNodeId: target.id,
      launchNodeId: node.id,
      launchTimeS: this.race.timeOfDayS,
      launchHeadingRad: facing,
      maxAltM: node.pos.y,
      distanceM: 0,
      ap: {
        mode: 'cruise',
        timerS: 0,
        circleDir: 1,
        liftAvg: 0,
        tried: [],
        landing: null,
        aim: null,
        avoid: null,
      },
    };
    a.flight = flight;
    a.mode = 'flying';
    a.nodeId = null;
    a.current = null;
    this.emit({ type: 'takeoff', athleteId: a.id, level: node.launch.level, nodeId: node.id });
  }

  // ---- フライト ----

  private updateFlight(a: AthleteState, dt: number): void {
    const f = a.flight;
    if (!f) return;
    const t = this.race.timeOfDayS;
    const rng = this.rngAthlete.get(a.id) as Rng;

    // 突風（オート：処理成功率で判定。手動の当て舵は P2）
    if (this.tickCount % 4 === 0 && !f.stall) {
      const rate = this.weather.gustRatePerMin(
        f.pos.x,
        f.pos.y,
        f.pos.z,
        t,
        GUST.refWindKmh,
        GUST.baseRatePerMin,
      );
      if (rollWithLuck(rng, a.stats, Math.min(1, rate / 60))) {
        const success = statLerp(a.stats.glider, GUST.autoSuccess[0], GUST.autoSuccess[1]);
        const failed = rollWithLuck(rng, a.stats, 1 - success);
        this.emit({ type: 'gust', athleteId: a.id, recovered: !failed });
        if (failed) {
          const w = this.weather.windAt(f.pos.x, f.pos.y, f.pos.z, t, this.tmpWind);
          const deep = Math.hypot(w.x, w.z) * 3.6 >= GUST.strongWindKmh;
          f.stall = {
            timerS: deep ? rng.range(FLIGHT.deepStallS[0], FLIGHT.deepStallS[1]) : FLIGHT.stallRecoverS,
            deep,
          };
          this.emit({ type: 'stall', athleteId: a.id, deep });
        }
      }
    }

    // CPU は、途中の目標の上にまだ高さが残っていれば、降りずに次の CP へ向かう
    if (!a.isPlayer && f.ap.mode === 'final' && this.race.phase === 'running') {
      const cpId = this.course.nodeIds[a.nextCp] as number;
      const nearT = Math.hypot(f.target.x - f.pos.x, f.target.z - f.pos.z) < 1500;
      const agl = f.pos.y - this.terrain.heightAt(f.pos.x, f.pos.z);
      if (nearT && f.targetNodeId !== cpId && agl > 600 && this.weather.thermalRatio(t) > 0.3) {
        const cp = this.graph.nodes[cpId] as NodeData;
        f.target = { x: cp.pos.x, z: cp.pos.z };
        f.targetNodeId = cpId;
        f.ap.mode = 'cruise';
        a.plan = [];
      }
    }

    const prevMode = f.ap.mode;
    const climbers: Vec2[] = [];
    for (const o of this.athletes) {
      if (o === a || !o.flight || o.flight.ap.mode !== 'climb') continue;
      if (Math.hypot(o.flight.pos.x - f.pos.x, o.flight.pos.z - f.pos.z) < 5000) climbers.push(o.flight.pos);
    }
    const sf = sinkFactor(a.stats);
    autopilot(
      f,
      {
        weather: this.weather,
        terrain: this.terrain,
        timeOfDayS: t,
        cloudBaseM: this.weather.cloudBaseAt(t),
        climbers,
        sinkFactor: sf,
        style: a.autoStyle,
        mustLand: this.race.phase === 'sunsetLanding',
      },
      dt,
      this.input,
    );
    const w = this.weather.windAt(f.pos.x, f.pos.y, f.pos.z, t, this.tmpWind);
    const lift = this.weather.liftAt(f.pos.x, f.pos.y, f.pos.z, t);
    stepFlightPhysics(f, this.input, { wx: w.x, wz: w.z, w: lift }, dt, sf);

    // アシスト：斜面の近くで旋回しているときは、円を斜面の外側（下り方向）へずらす
    if (f.circlingS > 2) {
      const g = this.terrain.gradientAt(f.pos.x, f.pos.z);
      const gl = Math.hypot(g.gx, g.gz);
      if (gl > 0.1) {
        const upX = g.gx / gl;
        const upZ = g.gz / gl;
        const uphill = this.terrain.heightAt(f.pos.x + upX * 60, f.pos.z + upZ * 60);
        if (uphill > f.pos.y - 40) {
          f.pos.x -= upX * 1.5 * dt;
          f.pos.z -= upZ * 1.5 * dt;
        }
      }
    }
    // アシスト：旋回中は、上昇の強い側（サーマルの芯）へ円を少しずつずらす
    if (f.circlingS > 4) {
      const th = this.weather.nearestThermal(f.pos.x, f.pos.y, f.pos.z, t);
      if (th && th.r < th.R * 2.5 && th.r > 5) {
        const pull = Math.min(2.0, th.r / 8) * dt;
        f.pos.x += (th.dx / th.r) * pull;
        f.pos.z += (th.dz / th.r) * pull;
      }
    }

    if (prevMode !== 'climb' && f.ap.mode === 'climb') {
      this.climbStart.set(a.id, f.pos.y);
      this.emit({ type: 'thermalEntered', athleteId: a.id, climbMs: Math.round(f.ap.liftAvg * 10) / 10 });
    } else if (prevMode === 'climb' && f.ap.mode !== 'climb') {
      const gain = f.pos.y - (this.climbStart.get(a.id) ?? f.pos.y);
      if (gain > 800) this.emit({ type: 'bigClimb', athleteId: a.id, gainM: Math.round(gain) });
    }

    a.stamina = Math.min(100, a.stamina + (STAMINA.flightRecoverPerHour * dt) / 3600);
    a.pos.x = f.pos.x;
    a.pos.y = f.pos.y;
    a.pos.z = f.pos.z;

    const ground = this.terrain.heightAt(f.pos.x, f.pos.z);
    if (f.pos.y <= ground) this.touchdown(a, ground);
  }

  private touchdown(a: AthleteState, ground: number): void {
    const f = a.flight as FlightState;
    const rng = this.rngAthlete.get(a.id) as Rng;
    const crash = f.stall !== null || f.varioMs < -FLIGHT.crashSinkMs;
    f.pos.y = ground;
    a.pos = { x: f.pos.x, y: ground, z: f.pos.z };
    if (crash) {
      this.useStamina(a, STAMINA.crash);
      a.day.crashes++;
      this.emit({ type: 'crashed', athleteId: a.id });
    }
    const { nodeId, created } = ensureNodeAt(this.graph, this.terrain, f.pos.x, f.pos.z);
    if (created) this.emit({ type: 'tempNodeAdded', nodeId });
    const planned = Math.hypot(f.target.x - f.pos.x, f.target.z - f.pos.z) <= FLIGHT.unplannedLandingM;
    const biome = this.terrain.biomeAt(f.pos.x, f.pos.z);
    let pack: number = PACK.normalS;
    if (biome === Biome.Forest && rollWithLuck(rng, a.stats, PACK.treeChance)) {
      pack = PACK.treeS;
      this.emit({ type: 'treeLanding', athleteId: a.id });
    } else if (biome !== Biome.Meadow && biome !== Biome.Road) pack = PACK.roughS;
    a.day.flights++;
    a.day.longestFlightM = Math.max(a.day.longestFlightM, f.distanceM);
    this.emit({ type: 'landed', athleteId: a.id, planned, distanceM: Math.round(f.distanceM), nodeId });
    // 予定の半分も飛べなかった離陸地は、しばらく選ばない（CPU がその日の条件を学ぶ）
    const launch = this.graph.nodes[f.launchNodeId];
    const plannedDist = launch ? Math.hypot(f.target.x - launch.pos.x, f.target.z - launch.pos.z) : 0;
    const flown = launch ? Math.hypot(f.pos.x - launch.pos.x, f.pos.z - launch.pos.z) : 0;
    if (f.distanceM < 1000 || flown < plannedDist * 0.5) {
      a.blockedLaunches.push({ nodeId: f.launchNodeId, untilS: this.race.activeRaceTimeS + 2 * 3600 });
    }
    a.flight = null;
    a.nodeId = nodeId;
    a.mode = 'packing';
    a.timerS = pack * prepPackFactor(a.stats);
    a.current = null;
    // CP を目標にしたフライトで CP に降りられなかったら（手前に降りた・届かなかった）、まず CP まで歩く。
    // 予約の続きは CP から先の区間なので、そのまま続けると CP を飛ばしてしまう
    const cpNode = this.course.nodeIds[a.nextCp];
    const missedCp = f.targetNodeId === cpNode && nodeId !== cpNode;
    if (!planned) {
      if (a.isPlayer) {
        if (missedCp) a.plan.unshift({ kind: 'hike', to: cpNode });
        this.race.pause = { reason: 'unplannedLanding', athleteId: a.id };
        this.emit({ type: 'forcedPause', athleteId: a.id });
      } else a.plan = [];
    } else if (missedCp) {
      a.plan.unshift({ kind: 'hike', to: cpNode });
    }
  }

  // ---- 進捗・順位 ----

  private updateProgress(a: AthleteState): void {
    if (a.finishTimeS !== null) return;
    const last = this.course.points.length - 1;
    const k = a.nextCp;
    const cp = this.course.points[k] as Vec2;
    const d = Math.hypot(a.pos.x - cp.x, a.pos.z - cp.z);
    const onFootOk = !COURSE_RULES.cpOnFoot || a.mode !== 'flying';
    if (k < last && d <= COURSE_RULES.cpRadiusM && onFootOk) {
      a.nextCp++;
      const cpNode = this.course.nodeIds[k];
      a.plan = a.plan.filter((s) => !(s.kind === 'hike' && s.to === cpNode));
      this.emit({ type: 'checkpointPassed', athleteId: a.id, cp: k });
      // CPU は CP を空中で通過したら、着陸せずに次の CP へ向かう
      if (!a.isPlayer && a.flight && a.flight.targetNodeId === cpNode) {
        const nextId = this.course.nodeIds[a.nextCp] as number;
        const next = this.graph.nodes[nextId] as NodeData;
        a.flight.target = { x: next.pos.x, z: next.pos.z };
        a.flight.targetNodeId = nextId;
        a.plan = [];
        // 目標の上で降り始めていても、高さが残っていれば飛び続ける（日没後はオートが着陸に戻す）
        if (a.flight.ap.mode === 'final' || a.flight.ap.mode === 'landing') {
          a.flight.ap.mode = 'cruise';
          a.flight.ap.landing = null;
        }
      }
    } else if (k === last && d <= COURSE_RULES.goalRadiusM && a.mode !== 'flying') {
      a.finishTimeS = this.race.activeRaceTimeS;
      a.mode = 'finished';
      a.plan = [];
      a.route = null;
      a.current = null;
      const rank = this.athletes.filter((o) => o.finishTimeS !== null).length;
      this.emit({ type: 'goalReached', athleteId: a.id, rank });
    }
    a.progressM = progressOf(this.course, a);
    if (a.progressM > a.bestProgressM) {
      a.bestProgressM = a.progressM;
      const lastLogged = a.progressLog[a.progressLog.length - 2] ?? 0;
      if (a.bestProgressM - lastLogged >= COURSE_RULES.progressLogStepM || a.finishTimeS !== null) {
        a.progressLog.push(a.bestProgressM, this.race.activeRaceTimeS);
      }
    }
  }

  private updateStandings(): void {
    const prev = this.race.standings;
    const next = computeStandings(this.course, this.athletes, this.race.activeRaceTimeS);
    // 首位交代は、前の首位に 300m 以上の差をつけたときだけ（僅差で入れ替わり続けないように）
    const top = next[0];
    const prevLeader = this.race.leaderId;
    if (top && top.athleteId !== prevLeader) {
      const prevRow = next.find((s) => s.athleteId === prevLeader);
      const clear = !prevRow || top.finished || top.progressM - prevRow.progressM > 300;
      if (clear) {
        if (prevLeader !== null) this.emit({ type: 'leadChange', athleteId: top.athleteId });
        this.race.leaderId = top.athleteId;
      }
    }
    const player = this.athletes.find((a) => a.isPlayer);
    if (player && prev.length > 0) {
      const rankOf = (list: typeof next, id: string): number =>
        list.find((s) => s.athleteId === id)?.rank ?? 0;
      const pPrev = rankOf(prev, player.id);
      const pNow = rankOf(next, player.id);
      for (const o of this.athletes) {
        if (o === player) continue;
        const oPrev = rankOf(prev, o.id);
        const oNow = rankOf(next, o.id);
        if (pPrev > oPrev && pNow < oNow)
          this.emit({ type: 'overtake', athleteId: player.id, passedId: o.id });
        if (pPrev < oPrev && pNow > oNow)
          this.emit({ type: 'overtake', athleteId: o.id, passedId: player.id });
      }
    }
    this.race.standings = next;
  }

  // ---- 日の進行 ----

  private beginSunset(): void {
    this.race.phase = 'sunsetLanding';
    this.emit({ type: 'sunset' });
    for (const a of this.athletes) {
      if (a.mode === 'preparing' || a.mode === 'waitingWind') this.abortTakeoff(a, 'sunset');
    }
  }

  private endDay(): void {
    if (this.race.phase === 'dayEnd' || this.race.phase === 'finished') return;
    for (const a of this.athletes) {
      if (a.finishTimeS !== null) continue;
      if (a.mode === 'flying' && a.flight)
        this.touchdown(a, this.terrain.heightAt(a.flight.pos.x, a.flight.pos.z));
      this.race.pause = null;
      if (a.mode === 'hiking' && a.route) {
        const arc = a.route.arcs[a.route.index];
        const edge = arc ? this.graph.edges[arc.edgeId] : undefined;
        const prefer = edge ? [edge.a, edge.b] : [];
        const { nodeId, created } = ensureNodeAt(this.graph, this.terrain, a.pos.x, a.pos.z, prefer);
        if (created) this.emit({ type: 'tempNodeAdded', nodeId });
        if (a.current) a.plan.unshift(a.current);
        a.nodeId = nodeId;
        a.pos = { ...(this.graph.nodes[nodeId] as NodeData).pos };
      } else if (a.current && a.isPlayer) a.plan.unshift(a.current);
      a.route = null;
      a.current = null;
      a.timerS = 0;
      a.mode = 'bivouac';
      if (!a.isPlayer) a.plan = [];
    }
    this.updateStandings();
    this.race.phase = 'dayEnd';
    this.emit({ type: 'dayEnd' });
    if (this.race.dayIndex >= this.profiles.length - 1) this.endRace();
  }

  private endRace(): void {
    if (this.race.phase === 'finished') return;
    this.updateStandings();
    this.race.phase = 'finished';
    this.emit({ type: 'raceEnd' });
  }

  private startNextDay(): void {
    const race = this.race;
    race.dayIndex++;
    race.timeOfDayS = TIME.dayStartS;
    race.phase = 'planning';
    race.pause = null;
    this.forecast = this.weather.startDay(
      this.profiles[race.dayIndex] as DayProfile,
      race.dayIndex,
      this.rngWeather,
      TIME.dayStartS,
    );
    for (const a of this.athletes) {
      a.day = { flights: 0, longestFlightM: 0, crashes: 0, startProgressM: a.progressM };
      if (a.finishTimeS !== null) continue;
      a.stamina = Math.min(
        100,
        a.stamina + (100 - a.stamina) * STAMINA.nightRecoverRatio * nightRecoveryFactor(a.stats),
      );
      a.mode = 'idle';
      a.blockedLaunches = [];
    }
    this.nextDecisionAt.clear();
    this.emit({ type: 'dayStart' });
    this.planCpus();
  }

  // ---- CPU ----

  private planCpus(): void {
    for (const a of this.athletes)
      if (!a.isPlayer && a.finishTimeS === null && a.plan.length === 0) this.decideCpu(a);
  }

  private decideCpu(a: AthleteState): void {
    if (a.isPlayer || a.finishTimeS !== null) return;
    const now = this.race.activeRaceTimeS;
    if ((this.nextDecisionAt.get(a.id) ?? -Infinity) > now) return;
    const d = decidePlan(a, {
      graph: this.graph,
      course: this.course,
      forecast: this.forecast,
      weather: this.weather,
      timeOfDayS: this.race.timeOfDayS,
      raceTimeS: now,
      rng: this.rngCpu.get(a.id) as Rng,
      personality: this.personalities.get(a.id) as Personality,
    });
    if (!d) {
      this.nextDecisionAt.set(a.id, now + 600);
      return;
    }
    a.plan = d.steps;
    this.emit({ type: 'cpuDecision', athleteId: a.id, reason: d.reason });
  }

  /** 雷雨の前触れ・発生を見た CPU は、次のノードで計画を立て直す */
  private flagStormReplan(x: number, z: number): void {
    for (const a of this.athletes) {
      if (a.isPlayer || a.finishTimeS !== null || a.mode === 'flying') continue;
      const hasFlight = a.current?.kind === 'flight' || a.plan.some((s) => s.kind === 'flight');
      if (!hasFlight || Math.hypot(a.pos.x - x, a.pos.z - z) > 30_000) continue;
      if (a.mode === 'hiking') this.replanAtNode.add(a.id);
      else if (a.mode === 'preparing' || a.mode === 'waitingWind' || a.mode === 'idle') {
        a.mode = 'idle';
        a.current = null;
        a.plan = [];
      }
    }
  }
}
